import { Card, CardContent, CardHeader, Divider } from "@mui/material";
import { FormControl, InputLabel, ListItem, ListItemText, MenuItem, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { PasswordConfirmationDialog } from "../dialogs/PasswordConfirmationDialog";
import { CompanySetting, restoreCompanySettings, updateCompanySettings, WarningLevel } from "../companySettingsAPI";
import { useAppDispatch } from "../../../../app/hooks";
import { showSuccess, showError } from "../../../notifications/notificationsSlice";
import { CardActionButtons } from "./CardActionButtons";
import { Localized } from "@fluent/react";
import { RestoreSettingsPasswordConfirmationDialog } from "../dialogs/RestoreSettingsPasswordConfirmationDialog";


interface CompanyDevicesSettingCardProps {
    companySettings: CompanySetting[];
    onUpdate: (updatedSettings: CompanySetting[]) => void;
}

const DEVICE_WARNING_LEVELS: WarningLevel[] = ["INFO", "WARNING", "ERROR", "NONE"];

const DEVICE_NUMBER_COMPANY_WARNING_LEVEL_CODE = "DEVICE_NUMBER_NONUNIQUE_COMPANY_WARNING_LEVEL";
const DEVICE_NUMBER_SUPPLIER_WARNING_LEVEL_CODE = "DEVICE_NUMBER_NONUNIQUE_SUPPLIER_WARNING_LEVEL";
const DEVICE_NUMBER_STRUCTURE_WARNING_LEVEL_CODE = "DEVICE_NUMBER_NONUNIQUE_STRUCTURE_WARNING_LEVEL";
const DEVICE_NUMBER_STRUCTURE_SUPPLIER_WARNING_LEVEL_CODE = "DEVICE_NUMBER_NONUNIQUE_STRUCT_SUPP_WARNING_LEVEL";

const getWarningLevel = (setting: CompanySetting | undefined): WarningLevel => {
    const upperValue = setting?.value?.toUpperCase();

    if (upperValue === "INFO" || upperValue === "WARNING" || upperValue === "ERROR" || upperValue === "NONE") {
        return upperValue;
    }

    return "NONE";
};

export const CompanyDevicesSettingCard: React.FC<CompanyDevicesSettingCardProps> = ({ companySettings, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const dispatch = useAppDispatch()
    const [serverError, setServerError] = useState("")

    const orgDeviceNumberCompanyWarningLevelSetting = companySettings.find(setting => setting.code === DEVICE_NUMBER_COMPANY_WARNING_LEVEL_CODE);
    const orgDeviceNumberSupplierWarningLevelSetting = companySettings.find(setting => setting.code === DEVICE_NUMBER_SUPPLIER_WARNING_LEVEL_CODE);
    const orgDeviceNumberStructureWarningLevelSetting = companySettings.find(setting => setting.code === DEVICE_NUMBER_STRUCTURE_WARNING_LEVEL_CODE);
    const orgDeviceNumberStructureSupplierWarningLevelSetting = companySettings.find(setting => setting.code === DEVICE_NUMBER_STRUCTURE_SUPPLIER_WARNING_LEVEL_CODE);

    const [deviceNumberCompanyWarningLevelSetting, setDeviceNumberCompanyWarningLevelSetting] = useState<CompanySetting | undefined>(orgDeviceNumberCompanyWarningLevelSetting);
    const [deviceNumberSupplierWarningLevelSetting, setDeviceNumberSupplierWarningLevelSetting] = useState<CompanySetting | undefined>(orgDeviceNumberSupplierWarningLevelSetting);
    const [deviceNumberStructureWarningLevelSetting, setDeviceNumberStructureWarningLevelSetting] = useState<CompanySetting | undefined>(orgDeviceNumberStructureWarningLevelSetting);
    const [deviceNumberStructureSupplierWarningLevelSetting, setDeviceNumberStructureSupplierWarningLevelSetting] = useState<CompanySetting | undefined>(orgDeviceNumberStructureSupplierWarningLevelSetting);

    const editableDeviceWarningSettings = [
        deviceNumberCompanyWarningLevelSetting,
        deviceNumberSupplierWarningLevelSetting,
        deviceNumberStructureWarningLevelSetting,
        deviceNumberStructureSupplierWarningLevelSetting
    ].filter(Boolean) as CompanySetting[];

    const isDefault = editableDeviceWarningSettings.length > 0 && editableDeviceWarningSettings.every(setting => setting.isDefault === true);

    useEffect(() => {
        setDeviceNumberCompanyWarningLevelSetting(orgDeviceNumberCompanyWarningLevelSetting);
        setDeviceNumberSupplierWarningLevelSetting(orgDeviceNumberSupplierWarningLevelSetting);
        setDeviceNumberStructureWarningLevelSetting(orgDeviceNumberStructureWarningLevelSetting);
        setDeviceNumberStructureSupplierWarningLevelSetting(orgDeviceNumberStructureSupplierWarningLevelSetting);
    }, [
        companySettings,
        isEditing,
        orgDeviceNumberCompanyWarningLevelSetting,
        orgDeviceNumberSupplierWarningLevelSetting,
        orgDeviceNumberStructureWarningLevelSetting,
        orgDeviceNumberStructureSupplierWarningLevelSetting
    ]);


    const handleRestore = () => {
        setRestoreDialogOpen(true);
    };
    
    const handleSave = () => {
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setServerError("")
    };

    const handleCloseRestore = () => {
        setRestoreDialogOpen(false);
        setServerError("")
    };
    
    const handleConfirmRestore = async (password: string) => {
        if (password) {
            setSaving(true)
            setServerError("")

            try {
                const result =
                    await dispatch(restoreCompanySettings({
                        password: password,
                        settings: [
                            DEVICE_NUMBER_COMPANY_WARNING_LEVEL_CODE,
                            DEVICE_NUMBER_SUPPLIER_WARNING_LEVEL_CODE,
                            DEVICE_NUMBER_STRUCTURE_WARNING_LEVEL_CODE,
                            DEVICE_NUMBER_STRUCTURE_SUPPLIER_WARNING_LEVEL_CODE
                        ]
                    }))

                if (restoreCompanySettings.fulfilled.match(result)) {
                    const settings = result.payload as CompanySetting[];
                    onUpdate(settings);
                    setDeviceNumberCompanyWarningLevelSetting(settings.find(f => f.code === DEVICE_NUMBER_COMPANY_WARNING_LEVEL_CODE));
                    setDeviceNumberSupplierWarningLevelSetting(settings.find(f => f.code === DEVICE_NUMBER_SUPPLIER_WARNING_LEVEL_CODE));
                    setDeviceNumberStructureWarningLevelSetting(settings.find(f => f.code === DEVICE_NUMBER_STRUCTURE_WARNING_LEVEL_CODE));
                    setDeviceNumberStructureSupplierWarningLevelSetting(settings.find(f => f.code === DEVICE_NUMBER_STRUCTURE_SUPPLIER_WARNING_LEVEL_CODE));
                    setRestoreDialogOpen(false);
                    setIsEditing(false);
                    dispatch(showSuccess("saved"))
                }
                else {
                    let errorMsg = "error"

                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }

    const handleConfirmSave = async (password: string) => {
        if (password) {
            setSaving(true)
            setServerError("")

            try {
                const updatedSettings: CompanySetting[] = [
                    deviceNumberCompanyWarningLevelSetting,
                    deviceNumberSupplierWarningLevelSetting,
                    deviceNumberStructureWarningLevelSetting,
                    deviceNumberStructureSupplierWarningLevelSetting
                ].filter(Boolean) as CompanySetting[];

                const result =
                    await dispatch(updateCompanySettings({
                        password: password,
                        settings: updatedSettings
                    }))

                if (updateCompanySettings.fulfilled.match(result)) {
                    const settings = result.payload as CompanySetting[];
                    onUpdate(settings)
                    setDeviceNumberCompanyWarningLevelSetting(settings.find(f => f.code === DEVICE_NUMBER_COMPANY_WARNING_LEVEL_CODE));
                    setDeviceNumberSupplierWarningLevelSetting(settings.find(f => f.code === DEVICE_NUMBER_SUPPLIER_WARNING_LEVEL_CODE));
                    setDeviceNumberStructureWarningLevelSetting(settings.find(f => f.code === DEVICE_NUMBER_STRUCTURE_WARNING_LEVEL_CODE));
                    setDeviceNumberStructureSupplierWarningLevelSetting(settings.find(f => f.code === DEVICE_NUMBER_STRUCTURE_SUPPLIER_WARNING_LEVEL_CODE));
                    setDialogOpen(false);
                    setIsEditing(false);
                    dispatch(showSuccess("saved"))
                }
                else {
                    let errorMsg = "error"

                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }

    const handleDeviceWarningLevelChange = (
        settingSetter: React.Dispatch<React.SetStateAction<CompanySetting | undefined>>,
        value: WarningLevel
    ) => {
        settingSetter(prev => prev ? { ...prev, value } : prev);
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    return (
        <Card sx={{ maxWidth: 500, borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
                title={<Localized id="company-settings-devices">
                    <span>_Urządzenia</span>
                </Localized>}
                sx={{
                    textAlign: "center",
                    bgcolor: "primary.main",
                    color: "white",
                    py: 1,
                    "& .MuiCardHeader-title": { fontSize: "1rem" },
                }}
            />
            <CardContent>
                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                        primary={<>
                            <Localized id="company-settings-devices-warning-level-company">
                                <span>_Poziom ostrzegania dla nieunikalnego numeru urządzenia w firmie</span>
                            </Localized>
                        </>}
                    />
                    <FormControl sx={{ minWidth: 180 }} size="small" disabled={!isEditing || deviceNumberCompanyWarningLevelSetting?.isFixed || !deviceNumberCompanyWarningLevelSetting}>
                        <InputLabel id="device-number-company-warning-level-label">
                            <Localized id="company-settings-devices-warning-level">
                                <span>_Poziom ostrzegania</span>
                            </Localized>
                        </InputLabel>
                        <Select
                            labelId="device-number-company-warning-level-label"
                            value={getWarningLevel(deviceNumberCompanyWarningLevelSetting)}
                            label={<Localized id="company-settings-devices-warning-level"><span>_Poziom ostrzegania</span></Localized>}
                            onChange={(e) => handleDeviceWarningLevelChange(setDeviceNumberCompanyWarningLevelSetting, e.target.value as WarningLevel)}
                        >
                            {DEVICE_WARNING_LEVELS.map(level => (
                                <MenuItem key={level} value={level}>
                                    <Localized id={`company-settings-warning-level-${level}`}>{level}</Localized>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </ListItem>

                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                        primary={<>
                            <Localized id="company-settings-devices-warning-level-supplier">
                                <span>_Poziom ostrzegania dla nieunikalnego numeru urządzenia u dostawcy</span>
                            </Localized>
                        </>}
                    />
                    <FormControl sx={{ minWidth: 180 }} size="small" disabled={!isEditing || deviceNumberSupplierWarningLevelSetting?.isFixed || !deviceNumberSupplierWarningLevelSetting}>
                        <InputLabel id="device-number-supplier-warning-level-label">
                            <Localized id="company-settings-devices-warning-level">
                                <span>_Poziom ostrzegania</span>
                            </Localized>
                        </InputLabel>
                        <Select
                            labelId="device-number-supplier-warning-level-label"
                            value={getWarningLevel(deviceNumberSupplierWarningLevelSetting)}
                            label={<Localized id="company-settings-devices-warning-level"><span>_Poziom ostrzegania</span></Localized>}
                            onChange={(e) => handleDeviceWarningLevelChange(setDeviceNumberSupplierWarningLevelSetting, e.target.value as WarningLevel)}
                        >
                            {DEVICE_WARNING_LEVELS.map(level => (
                                <MenuItem key={level} value={level}>
                                    <Localized id={`company-settings-warning-level-${level}`}>{level}</Localized>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </ListItem>

                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                        primary={<>
                            <Localized id="company-settings-devices-warning-level-structure">
                                <span>_Poziom ostrzegania dla nieunikalnego numeru urządzenia w strukturze</span>
                            </Localized>
                        </>}
                    />
                    <FormControl sx={{ minWidth: 180 }} size="small" disabled={!isEditing || deviceNumberStructureWarningLevelSetting?.isFixed || !deviceNumberStructureWarningLevelSetting}>
                        <InputLabel id="device-number-structure-warning-level-label">
                            <Localized id="company-settings-devices-warning-level">
                                <span>_Poziom ostrzegania</span>
                            </Localized>
                        </InputLabel>
                        <Select
                            labelId="device-number-structure-warning-level-label"
                            value={getWarningLevel(deviceNumberStructureWarningLevelSetting)}
                            label={<Localized id="company-settings-devices-warning-level"><span>_Poziom ostrzegania</span></Localized>}
                            onChange={(e) => handleDeviceWarningLevelChange(setDeviceNumberStructureWarningLevelSetting, e.target.value as WarningLevel)}
                        >
                            {DEVICE_WARNING_LEVELS.map(level => (
                                <MenuItem key={level} value={level}>
                                    <Localized id={`company-settings-warning-level-${level}`}>{level}</Localized>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </ListItem>

                <ListItem sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                        primary={<>
                            <Localized id="company-settings-devices-warning-level-structure-supplier">
                                <span>_Poziom ostrzegania dla nieunikalnego numeru urządzenia w strukturze i u dostawcy</span>
                            </Localized>
                        </>}
                    />
                    <FormControl sx={{ minWidth: 180 }} size="small" disabled={!isEditing || deviceNumberStructureSupplierWarningLevelSetting?.isFixed || !deviceNumberStructureSupplierWarningLevelSetting}>
                        <InputLabel id="device-number-structure-supplier-warning-level-label">
                            <Localized id="company-settings-devices-warning-level">
                                <span>_Poziom ostrzegania</span>
                            </Localized>
                        </InputLabel>
                        <Select
                            labelId="device-number-structure-supplier-warning-level-label"
                            value={getWarningLevel(deviceNumberStructureSupplierWarningLevelSetting)}
                            label={<Localized id="company-settings-devices-warning-level"><span>_Poziom ostrzegania</span></Localized>}
                            onChange={(e) => handleDeviceWarningLevelChange(setDeviceNumberStructureSupplierWarningLevelSetting, e.target.value as WarningLevel)}
                        >
                            {DEVICE_WARNING_LEVELS.map(level => (
                                <MenuItem key={level} value={level}>
                                    <Localized id={`company-settings-warning-level-${level}`}>{level}</Localized>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </ListItem>

                <Divider sx={{ width: "100%", mt: 3 }} />
                <CardActionButtons
                    isDefault={isDefault}
                    isEditing={isEditing}
                    onRestore={handleRestore}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onEdit={() => setIsEditing(true)}
                />

            </CardContent>
            {dialogOpen &&
                <PasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleClose}
                    onConfirm={handleConfirmSave}
                />
            }
            {restoreDialogOpen &&
                <RestoreSettingsPasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleCloseRestore}
                    onConfirm={handleConfirmRestore}
                />
            }
        </Card>

    );
};
